/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { ButtonComponent } from '@app/app/shared/styleguide/components/buttons/default/button.component';
import { LucideBookmark, LucideCircleAlert, LucideCircleCheck, LucideDynamicIcon, LucidePencil, LucideTrash2 } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { V3ContentCardAction, V3ContentCardItem, V3ContentCardView } from './v3-content-card.model';

@Component({
  selector: 'ptl-v3-content-card',
  standalone: true,
  imports: [ButtonComponent, LucideDynamicIcon, TranslocoModule],
  templateUrl: './v3-content-card.component.html',
  styleUrls: ['./v3-content-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3ContentCardComponent {
  item = input.required<V3ContentCardItem>();
  view = input<V3ContentCardView>('grid');
  action = input<V3ContentCardAction | null>(null);
  saveable = input(false);
  saved = input(false);
  saveLabel = input('');
  progressSuffix = input('');
  reserveRowSpace = input(true);
  editActions = input(false);
  compact = input(false);

  cardClicked = output<V3ContentCardItem>();
  actionClicked = output<V3ContentCardItem>();
  saveToggled = output<V3ContentCardItem>();
  editClicked = output<V3ContentCardItem>();
  deleteClicked = output<V3ContentCardItem>();

  protected readonly LucideBookmark = LucideBookmark;
  protected readonly LucidePencil = LucidePencil;
  protected readonly LucideTrash2 = LucideTrash2;

  protected readonly showSave = computed(() => this.saveable() && !this.editActions());

  protected readonly isList = computed(() => this.view() === 'list');
  protected readonly hasThumbnail = computed(() => !!this.item().thumbnail);
  protected readonly coverBackground = computed(() => (this.hasThumbnail() ? null : this.item().gradient || null));
  protected readonly gradeLabel = computed(() => this.item().gradeLabel ?? null);
  protected readonly gradePassing = computed(() => this.item().gradePassing !== false);
  protected readonly gradeIcon = computed(() => (this.gradePassing() ? LucideCircleCheck : LucideCircleAlert));
  protected readonly hasProgress = computed(
    () => !this.gradeLabel() && this.item().progress !== null && this.item().progress !== undefined,
  );
  protected readonly progressValue = computed(() => Math.min(100, Math.max(0, Math.round(this.item().progress ?? 0))));
  protected readonly metaEntries = computed(() => this.item().meta ?? []);
  protected readonly tags = computed(() => this.item().tags ?? []);
  protected readonly showBadgeRow = computed(() => !!this.item().statusBadge || (this.isList() && !!this.item().typeLabel));

  onCardClick(): void {
    this.cardClicked.emit(this.item());
  }

  onActionClick(): void {
    this.actionClicked.emit(this.item());
  }

  onSaveClick(): void {
    this.saveToggled.emit(this.item());
  }

  onEditClick(): void {
    this.editClicked.emit(this.item());
  }

  onDeleteClick(): void {
    this.deleteClicked.emit(this.item());
  }
}
